import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import {
    AttendeeRegistrationStatus,
    AttendeeRegistrationStatusLabel,
    IAttendeeDetailsDTO,
    IAttendeeListItemDTO,
    IAttendeesDAO,
    IAttendeesListDTO,
    IButtonPropertyDTO,
    TGetAttendeesSortKeys,
} from '../../../../api/attendees.dao';
import { IOrganizationsDAO, IOrganizationsListDTO } from '../../../../api/organizations.dao';
import { IJobsMonitor } from '../../../../domain/jobs-monitor/jobs-monitor';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { OrganizationForm } from '../_organizations-list/_organization-form/organization-form';
import { SortOrder } from '../../../../api/shared.dto';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { EventExecutionRuntime } from '../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType, } from '../../../../api/jobs.dao';
import { MINIMAL_SEARCH_LENGTH } from '../../../../constants/common.constants';
import { isPathIncludesInCurrentRoute } from '../../../../infrastructure/utils/common.utils';
import { IRouter } from '../../../../infrastructure/router';
import { RouteParams, Routes } from '../../../../routes-table';
import { ICustomFieldsDTO } from '../../../../api/custom-fields.dao';
import { RegistrationConfigurationDAO } from '../../../../api/implementations/registration-configuration.dao';
import { AttendeeForm } from './_attendee-form/attendee-form';
import { AttendeeBulkUpdateForm } from './_attendee-form/attendee-bulk-update-form/attendee-bulk-update-form';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { OpenFrom, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { attendanceMethods } from '../../../../constants/attendee-values';
import { AttendeeColumnPopoverViewmodel } from '../../../global/attendee-column/attendee-column-popover/attendee-column-popover.viewmodel';

class AttendeeListViewModel {
    @mobx.observable
    public attendees: IAttendeeListItemDTO[] = [];

    @mobx.observable
    private fetchingAttendees: boolean;

    @mobx.observable
    private attendeeIdToEditPopupMap: { [key: string]: ISubWindowFacade };

    private readonly orgIdToEditAttendeesPopupsMap: { [organizationId: string]: ISubWindowFacade[] };

    @mobx.observable
    private sortOrder: SortOrder;

    @mobx.observable
    public attendeeCSVFile;

    @mobx.observable
    public accessCodesCSVFile;

    @mobx.observable
    public isUploadCSVModalOpen: boolean;

    @mobx.observable
    public isUploadAccessCodesCSVModalOpen: boolean;

    @mobx.observable
    private sortField: TGetAttendeesSortKeys;

    @mobx.observable
    public searchQuery: string;

    private paginationVM: PagingViewModel;

    public deleteAttendeeAlertKey: string;
    public fetchOrganizationsList: () => Promise<void>;

    public fetchMeetingsList: () => Promise<void>;

    public fetchRoomsWithConnections: () => Promise<void>;

    public fetchAssetsOrganizations: () => Promise<void>;

    @mobx.observable
    public customFields: ICustomFieldsDTO[] = [];

    @mobx.observable
    public selectedCustomField: ICustomFieldsDTO;

    @mobx.observable
    public selectedFilterStatuses = '';

    @mobx.observable
    public selectedFilterAttendanceMethods = '';

    @mobx.observable
    public attendeeSelectedIds: number[] = [];
    @mobx.observable
    private attendeeDetailsPopoverVM: AttendeeColumnPopoverViewmodel;

    public constructor(
        public readonly accountId: number,
        public readonly eventId: number,
        attendeeIdToEditPopupMap: { [key: string]: ISubWindowFacade },
        private readonly intl: IntlShape,
        @IoC.Inject private readonly attendeesDAO = IoC.Container.get(IAttendeesDAO),
        @IoC.Inject private readonly alertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly jobsMonitor: IJobsMonitor = IoC.Container.get(IJobsMonitor),
        @IoC.Inject private readonly organizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly routingService: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly registrationConfigurationDAO: RegistrationConfigurationDAO = IoC.Container.get(RegistrationConfigurationDAO),
        @IoC.Inject private readonly eventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),

    ) {
        this.attendeeIdToEditPopupMap = attendeeIdToEditPopupMap;
        this.sortField = 'organization.name';
        this.sortOrder = SortOrder.Asc;
        this.attendees = [];
        this.searchQuery = '';
        this.paginationVM = new PagingViewModel();

        this.eventExecutionRuntime.onComplete(JobType.AttendeesUpload, async () => {
            this.closeAllAttendeeModals();
            await this.fetchAttendees();
        });
        this.eventExecutionRuntime.onComplete(JobType.GenerateAccessCodes, async () => {
            await this.fetchAttendees();
            this.closeAllAttendeeModals();
        });
        this.eventExecutionRuntime.onComplete(JobType.GenerateOrganizationsLinks, async () => await this.fetchAttendees());
        this.eventExecutionRuntime.onComplete(JobType.EventBlueprintTrigger, async () => await this.fetchAttendees());

        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchAttendees);
        this.initAttendeDetailsPopoverVM();
    }

    public get attendeDetailsVM(): AttendeeColumnPopoverViewmodel {
        return this.attendeeDetailsPopoverVM;
    }

    @mobx.action
    public handleAllAttendeeSelect = (attendeeIds: number[], isSelected: boolean) => {
        if (isSelected) {
            this.attendeeSelectedIds = this.attendeeSelectedIds.filter(
                attendeeId => !attendeeIds.find(el => el === attendeeId)
            );
        } else {
            this.attendeeSelectedIds = [...new Set([...this.attendeeSelectedIds, ...attendeeIds])];
        }
    };

    @mobx.action
    public handleAttendeeSelect = (attendeeId: number, isSelected: boolean) => {
        if (isSelected) {
            this.attendeeSelectedIds = this.attendeeSelectedIds.filter(el => el !== attendeeId);
        } else {
            this.attendeeSelectedIds = [...this.attendeeSelectedIds, attendeeId];
        }
    };

    @mobx.action
    public clearAttendeeSelectedIds = () => this.attendeeSelectedIds = [];

    @mobx.action
    public fetchCustomFields = async () => {
        const response = await this.registrationConfigurationDAO.getFormRegistrationConfiguration(this.eventId, this.accountId);
        const result = response.fields.filter(tile => !tile.default).sort((a, b) => a.order - b.order);
        mobx.runInAction(() => {
            this.customFields = result;
            if (result.length) {
                if (this.selectedCustomField) {
                    this.selectedCustomField = result.find(el => el.customFieldId === this.selectedCustomField.customFieldId) || result[0];
                } else {
                    this.selectedCustomField = result[0];
                }
            } else {
                this.selectedCustomField = null;
            }
        });
    };

    @mobx.action
    public onCustomFieldChange = (customField: ICustomFieldsDTO) => {
        this.selectedCustomField = customField;
    };

    public setFetchOrganizationsList = (fetchOrganizations: () => Promise<void>) => {
        this.fetchOrganizationsList = fetchOrganizations;
    };

    public setFetchRoomsWithConnections = (fetchRoomsWithConnections: () => Promise<void>) => {
        this.fetchRoomsWithConnections = fetchRoomsWithConnections;
    };

    public setFetchMeetingsList = (fetchMeetingsList: () => Promise<void>) => {
        this.fetchMeetingsList = fetchMeetingsList;
    };

    public setAssetsOrganizationsList = (fetchAssetsOrganizations: () => Promise<void>) => {
        this.fetchAssetsOrganizations = fetchAssetsOrganizations;
    };

    public setDeleteAttendeeAlertKey = (deleteAttendeeAlertKey: string): void => {
        this.deleteAttendeeAlertKey = deleteAttendeeAlertKey;
    };

    public getPagingVM() {
        return this.paginationVM;
    }

    public isFetchingAttendees = () => {
        return this.fetchingAttendees;
    };

    @mobx.action
    public setSearchQuery = (query: string) => {
        this.searchQuery = query;

        if (query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.paginationVM.selectPage(1);
            this.fetchAttendees();
        }
    };

    @mobx.action
    public clearSearchQuery = () => {
        this.searchQuery = '';
        this.paginationVM.selectPage(1);
        this.fetchAttendees();
    };

    public getAttendees = () => {
        return this.attendees;
    };

    public isAttendeeListEmpty = () => {
        return Boolean(this.attendees?.length === 0);
    };

    public downloadAttendeesCSV = async () => {
        await this.organizationsDAO.getOrganizationsCSV({ accountId: this.accountId, eventId: this.eventId });

        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_ATTENDEES_CSV, {
            'Open From': OpenFrom.Attendees,
        });
    };

    public downloadOrganizationsLinksCSV = async () => {
        try {
            await this.organizationsDAO.getOrganizationsLinksCSV({
                accountId: this.accountId,
                eventId: this.eventId
            });

            this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_PRESENTER_ORG_LINKS, {
                'Open From': OpenFrom.Attendees,
            });
        } catch (error) {
            const errorMessage = this.errorParsing.extractMessage(error);
            alertServerError('Error', errorMessage);
        }
    };

    @mobx.action
    public setAttendeeCSVFile = (file: File) => {
        this.attendeeCSVFile = file;
    };

    @mobx.action
    public setAccessCodesCSVFile = (file: File) => {
        this.accessCodesCSVFile = file;
    };

    @mobx.action.bound
    public setUploadCSVModalOpen = (value: boolean) => {
        this.isUploadCSVModalOpen = value;
        this.attendeeCSVFile = null;
    };

    @mobx.action.bound
    public setUploadAccessCodesCSVModalOpen = (value: boolean) => {
        this.isUploadAccessCodesCSVModalOpen = value;
        this.accessCodesCSVFile = null;
    };

    @mobx.action
    public importAttendeesCSV = async () => {
        const job = this.eventExecutionRuntime.getJobByType(JobType.AttendeesUpload);
        job.onComplete(async () => {
            this.closeAllAttendeeModals();
            await this.fetchAttendees();
        });

        try {
            await job.trigger({ file: this.attendeeCSVFile });
            this.setUploadCSVModalOpen(false);
        } catch {
            this.setUploadCSVModalOpen(true);
        }
    };


    @mobx.action
    public importAccessCodesCSV = async () => {
        const job = this.eventExecutionRuntime.getJobByType(JobType.AccessCodesUpload);
        try {
            await job.trigger({ file: this.accessCodesCSVFile });
            this.setUploadAccessCodesCSVModalOpen(false);
        } catch {
            this.setUploadAccessCodesCSVModalOpen(true);
        }
    };


    @mobx.action
    public fetchAttendees = async () => {
        this.fetchingAttendees = true;
        try {
            const result = await this.attendeesDAO.getAttendees({
                accountId: this.accountId,
                eventId: this.eventId,
                query: this.searchQuery?.trim() || '',
                sortOrder: this.sortOrder,
                sortField: this.sortField,
                registrationStatuses: this.selectedFilterStatuses,
                attendanceMethods: this.selectedFilterAttendanceMethods,
                size: this.paginationVM.getPageSize(),
                page: this.paginationVM.getCurrentPageForBE()
            });
            this.onFetchAttendeesSuccess(result);
        } catch (error) {
            this.onFetchAttendeesFail(error);
        }
    };

    @mobx.action
    public initialize(): void {
        this.onFetchAttendeesSuccess({
            content: [],
            totalElements: 0,
        });
    }

    @mobx.action
    public editAttendee = (
        attendeeId: number,
        openFrom: OpenFrom = OpenFrom.Attendees,
        saveCompletedCallback?: (attendee: IAttendeeDetailsDTO) => void
    ) => {
        let shallShowNewEditWindow = true;

        const alreadyShownSubWindow = this.attendeeIdToEditPopupMap[attendeeId];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            this.attendeeIdToEditPopupMap[attendeeId] = this.showAttendeeForm('Edit Attendee', openFrom, attendeeId, saveCompletedCallback);
        }
    };

    @mobx.action
    public bulkEditAttendees = (attendeeIds: number[]) => {
        let shallShowNewEditWindow = true;

        const alreadyShownSubWindow = this.attendeeIdToEditPopupMap['bulkAttendeeEdit'];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            this.attendeeIdToEditPopupMap['bulkAttendeeEdit'] = this.showBulkAttendeeForm(`Edit ${attendeeIds.length} Attendees`, attendeeIds);
        }
    };

    public addAttendee = (
        openFrom: OpenFrom = OpenFrom.Attendees,
        onAddNewAttendee?: (newAttendee: IAttendeeDetailsDTO) => void,
        organizationId?: number
    ) => this.showAttendeeForm(this.intl.formatMessage({
        id: 'oe.attendee.new',
        defaultMessage: '{prefix}New Attendee',
        description: 'Label text'
    }, {
        prefix: ''
    }), openFrom, undefined, onAddNewAttendee, undefined, organizationId);

    public closeEditAttendeeForm(id: string | number): void {
        if (this.attendeeIdToEditPopupMap[id]) this.attendeeIdToEditPopupMap[id].close();
    }

    public checkRouteAndFetch = async () => {
        const route = this.routingService.router.getState();
        if (isPathIncludesInCurrentRoute(Routes.OrganizationsList, route)) {
            await this.fetchOrganizationsList();
        }
        if (isPathIncludesInCurrentRoute(Routes.AttendeesList, route)) {
            await this.fetchAttendees();
        }
        if (isPathIncludesInCurrentRoute(Routes.Meetings, route)) {
            await this.fetchMeetingsList();
        }
        if (isPathIncludesInCurrentRoute(Routes.ConnectionsList, route)) {
            await this.fetchRoomsWithConnections();
        }
        if (isPathIncludesInCurrentRoute(Routes.Assets, route)) {
            await this.fetchAssetsOrganizations();
        }
    };

    public checkRouteAfterBulkUpdate = async () => {
        const route = this.routingService.router.getState();
        if (isPathIncludesInCurrentRoute(Routes.AttendeesList, route)) {
            await this.fetchAttendees();
        }
    };

    public isAttendeeEditFormShown(attendeeId: number) {
        return this.attendeeIdToEditPopupMap[attendeeId]
            && this.attendeeIdToEditPopupMap[attendeeId].getStatus() !== SubWindowStatus.Closed;
    }

    @mobx.action
    public setSortParams = (sortField: TGetAttendeesSortKeys, sortOrder: SortOrder) => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        this.fetchAttendees();
    };

    public getSortField = () => {
        return this.sortField;
    };

    public getSortOrder = () => {
        return this.sortOrder;
    };

    public composeUploadAttendeesButtonProperties(): IButtonPropertyDTO {
        return {
            text: this.intl.formatMessage(
                {
                    defaultMessage: 'Upload Attendees CSV',
                    description: 'Upload Attendees title',
                    id: 'oe.attendee.uploadAttendees'
                }
            ),
            disabled: !this.eventExecutionRuntime.isReady()
        };
    }

    public composeUploadAccessCodesButtonProperties(): IButtonPropertyDTO {
        return {
            text: this.intl.formatMessage(
                {
                    defaultMessage: 'Upload Access Codes CSV',
                    description: 'Upload Access codes title',
                    id: 'oe.attendee.uploadAccessCodes'
                }
            ),
            disabled: !this.eventExecutionRuntime.isReady()
        };
    }

    public composeGenerateAccessCodesButtonProperties(): IButtonPropertyDTO {
        return {
            text: 'Generate Access Codes',
            disabled: !this.eventExecutionRuntime.isReady() || this.isAttendeeListEmpty(),
        };
    }

    public composeGenerateOrganizationsLinksButtonProperties(): IButtonPropertyDTO {
        return {
            text: 'Generate Presenter Organization Links',
            disabled: !this.eventExecutionRuntime.isReady()
        };
    }

    @mobx.action
    public generateOrganizationsLinks = async () => {
        const job = this.eventExecutionRuntime.getJobByType(JobType.GenerateOrganizationsLinks);
        job.onComplete(async () => await this.fetchAttendees());

        await job.trigger();
    };

    @mobx.action
    public generateAccessCodes = async (): Promise<void> => {
        const job = this.eventExecutionRuntime.getJobByType(JobType.GenerateAccessCodes);
        job.onComplete(async () => {
            await this.fetchAttendees();
            this.closeAllAttendeeModals();
        });
        await job.trigger();
    };

    @mobx.action
    private onFetchAttendeesSuccess(data: IAttendeesListDTO) {
        this.attendees = data.content;
        this.fetchingAttendees = false;
        this.paginationVM.setTotalRecords(data.totalElements);
    }

    @mobx.action
    public removeAttendeeFromList(id: number): void {
        const index = this.attendees.findIndex(attendee => attendee.attendeeId === id);
        if (index !== -1) {
            this.attendees.splice(index, 1);
        }
    }

    @mobx.action
    public deleteAttendeeOnList(id: number): void {
        this.closeEditAttendeeForm(id);
        this.removeAttendeeFromList(id);
        this.attendeeSelectedIds = this.attendeeSelectedIds.filter(el => el !== id);
    }

    @mobx.action
    public onUpdateBulkList(): void {
        this.closeEditAttendeeForm('bulkAttendeeEdit');
    }

    @mobx.action
    private onFetchAttendeesFail(error) {
        this.fetchingAttendees = false;
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError('API Error fetching attendee records', errorMessage);
    }

    private closeAllAttendeeModals() {
        for (const attendeeId of Object.keys(this.attendeeIdToEditPopupMap)) {
            this.attendeeIdToEditPopupMap[attendeeId].close();
        }
    }

    public showCreateOrganization = (onOrganizationListUpdated?: (shouldUpdateOrganizatiosList: boolean) => Promise<IOrganizationsListDTO>) => {
        this.subWindowsService.showSubWindow({
            title: 'New Organization',
            componentToHost: OrganizationForm,
            componentToHostProps: {
                updateOrganizationOnList: () => {
                    const route = this.routingService.router.getState();
                    if (isPathIncludesInCurrentRoute(Routes.OrganizationsList, route)) {
                        this.fetchOrganizationsList?.();
                    }
                    onOrganizationListUpdated?.(true);
                },
                eventId: this.eventId,
                accountId: this.accountId,
                openFrom: OpenFrom.Attendees,
            },
        });
    };

    private showAttendeeForm = (
        title: string,
        openFrom: OpenFrom,
        attendeeId?: number,
        onAddNewAttendee?: (newAttendee: IAttendeeDetailsDTO) => void,
        onUpdateList?: () => void,
        organizationId?: number
    ) => this.subWindowsService.showSubWindow({
        componentToHost: AttendeeForm,
        componentToHostProps: {
            deleteAttendeeOnList: this.deleteAttendeeOnList.bind(this, attendeeId),
            setDeleteAttendeeAlertKey: key => this.setDeleteAttendeeAlertKey(key),
            saveCompletedCallback: (attendee: IAttendeeDetailsDTO) => {
                if (onAddNewAttendee) {
                    onAddNewAttendee(attendee);
                } else {
                    this.checkRouteAndFetch();
                }
                onUpdateList?.();
            },
            eventId: this.eventId,
            accountId: this.accountId,
            intl: this.intl,
            organizationId: organizationId,
            attendeeId: attendeeId,
            createOrganizationFromForm: this.showCreateOrganization,
            openFrom: openFrom,
        },
        title: title,
    });

    private showBulkAttendeeForm = (
        title: string,
        attendeeIds?: number[],
    ) => this.subWindowsService.showSubWindow({
        componentToHost: AttendeeBulkUpdateForm,
        componentToHostProps: {
            onUpdateBulkList: this.onUpdateBulkList.bind(this),
            intl: this.intl,
            attendeeIds: attendeeIds,
            saveBulkCompletedCallback: () => this.checkRouteAfterBulkUpdate()
        },
        title: title,
    });

    @mobx.action
    public clearAttendees = () => {
        this.attendees = [];
    };

    @mobx.action
    public handleChangeAttendanceMethodFilter = (attendanceMethodsValue: string): void => {
        this.selectedFilterAttendanceMethods = attendanceMethodsValue;
        this.fetchAttendees();
    };

    public getRegistrationStatuses = (): Promise<{ id: string; label: string }[]> => {
        return new Promise(resolve => {
            resolve(
                Object.values(AttendeeRegistrationStatus).map(registrationStatus => {
                    return {
                        id: registrationStatus,
                        label: AttendeeRegistrationStatusLabel[registrationStatus]
                    };
                })
            );
        });
    };

    @mobx.action
    public handleChangeRegistrationStatusFilter = (statuses: string) => {
        this.selectedFilterStatuses = statuses;
        this.fetchAttendees();
    };

    public getAttendanceMethods = (): { id: string; label: string }[] => {
        return attendanceMethods(this.intl).map(({ label, value }) => ({ id: value, label: label }));
    };

    @mobx.action
    public clearFilters = () => {
        this.selectedFilterStatuses = '';
        this.selectedFilterAttendanceMethods = '';
    };

    public trackCopyAttendeeInfoPopoverButtonMixPanel = (eventName: string, openFrom: OpenFrom): void => {
        this.trackingService.get().track(TRACKING_EVENTS.COPY_ATTENDEE_NAME_FROM_POPOVER, {
            [eventName]: openFrom,
        });
    };

    public initAttendeDetailsPopoverVM() {
        this.attendeeDetailsPopoverVM = new AttendeeColumnPopoverViewmodel(
            this.accountId,
            this.eventId,
            this.editAttendee,
            this.trackCopyAttendeeInfoPopoverButtonMixPanel
        );
    }
}

export { AttendeeListViewModel };
